/**
 * Advisor override dialog (QL-13).
 *
 * QL-414 — the override records that the client passed this test outside QP.
 * The quiz then counts as passed (also after the client used up their
 * attempts). The reason is mandatory: the PDF prints it as "External test
 * confirmed by {name} on {date}" with the reason, and the client sees it in
 * their flow.
 *
 * Advisor surface only; the public flow never mounts this.
 */

import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./quiz-override-dialog.i18n";

export type QuizOverrideDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  quizName: string;
  onConfirm: (reason: string) => void;
  isSaving?: boolean;
};

export function QuizOverrideDialog({
  open,
  onOpenChange,
  quizName,
  onConfirm,
  isSaving = false,
}: QuizOverrideDialogProps) {
  const t = useT(T);
  const [reason, setReason] = useState("");

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) setReason("");
        onOpenChange(next);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t.title}</DialogTitle>
          <DialogDescription>{t.body}</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="quiz-override-reason">{`${t.reason}: ${quizName}`}</Label>
          <Textarea
            id="quiz-override-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t.reasonPlaceholder}
            disabled={isSaving}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
            {t.cancel}
          </Button>
          <Button
            onClick={() => onConfirm(reason.trim())}
            disabled={!reason.trim()}
            loading={isSaving}
          >
            {t.confirm}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
